import React, { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'

// Interface for list box items
interface ListBoxItem {
  id: string | number
  name: string
  // Allow additional arbitrary properties while maintaining type-safety
  [key: string]: unknown
}

// Interface for component props
interface ListBoxWithCheckProps {
  label?: string
  values: ListBoxItem[]
  selected: ListBoxItem
  setSelected: (value: ListBoxItem) => void
  disabled?: boolean
}

export const ListBoxWithCheck: React.FC<ListBoxWithCheckProps> = ({
  label = '',
  values,
  selected,
  setSelected,
  disabled = false
}) => {
  return (
    <Listbox value={selected} onChange={disabled ? () => { } : setSelected} disabled={disabled}>
      {({ open }) => (
        <>
          <Listbox.Label className={clsx(
            'block text-sm font-medium',
            disabled ? 'text-gray-400' : 'text-gray-700'
          )}>
            {label && label}
          </Listbox.Label>
          <div className={clsx('relative', label ? 'mt-1' : '')}>
            <Listbox.Button className={clsx(
              'relative w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-left shadow-sm sm:text-sm',
              disabled
                ? 'cursor-not-allowed bg-gray-50 text-gray-400'
                : 'cursor-default bg-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500'
            )}>
              <span className='block truncate'>{selected.name}</span>
              <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
                <ChevronUpDownIcon
                  className={clsx(
                    'h-5 w-5',
                    disabled ? 'text-gray-300' : 'text-gray-400'
                  )}
                  aria-hidden='true'
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave='transition ease-in duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Listbox.Options className='absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
                {values.map(value => (
                  <Listbox.Option
                    key={value.id}
                    className={({ active }) =>
                      clsx(
                        active ? 'bg-sky-600 text-white' : 'text-slate-900',
                        'relative cursor-default select-none py-2 pl-8 pr-4'
                      )
                    }
                    value={value}
                  >
                    {({ selected, active }) => {
                      return (
                        <>
                          <span
                            className={clsx(
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate'
                            )}
                          >
                            {value.name}
                          </span>

                          {selected ? (
                            <span
                              className={clsx(
                                active ? 'text-white' : 'text-indigo-600',
                                'absolute inset-y-0 left-0 flex items-center pl-1.5'
                              )}
                            >
                              <CheckIcon
                                className='h-5 w-5'
                                aria-hidden='true'
                              />
                            </span>
                          ) : null}
                        </>
                      )
                    }}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  )
}
